import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(listRes.data);
      setUnread(countRes.data.count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openItem = async (n) => {
    try {
      await api.post(`/notifications/${n.notification_id}/read`);
    } catch {
      // ignore
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-slate-500 hover:text-brand-navy transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] min-h-[18px] bg-brand-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-brand-navy">Notifications</p>
            {unread > 0 && (
              <button data-testid="notifications-read-all" onClick={markAll} className="text-xs text-brand-turquoise hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Tout marquer lu
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.map((n) => (
              <button
                key={n.notification_id}
                data-testid={`notification-${n.notification_id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? "bg-brand-sky/40" : ""}`}
              >
                <p className="text-sm text-brand-navy">{n.text}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {n.created_at ? new Date(n.created_at).toLocaleString("fr-CA") : ""}
                </p>
              </button>
            ))}
            {items.length === 0 && (
              <p data-testid="notifications-empty" className="text-sm text-slate-400 text-center py-8">
                Aucune notification.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
